import {
	BackButton,
	LoadingState,
	PageContainer,
	PageHeader,
} from "@client/components/ui";
import { Button } from "@client/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@client/components/ui/card";
import { Input } from "@client/components/ui/input";
import { useAppForm } from "@client/components/ui/tanstack-form";
import { trpc } from "@client/lib/trpc-client";
import { UpdateReferralCodeSchema } from "@shared/entities/referral";
import { createFileRoute } from "@tanstack/react-router";
import { Check, Coins, Copy, Gift, Users } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

export const Route = createFileRoute("/(protected)/settings/referrals")({
	component: ReferralsPage,
});

function ReferralsPage() {
	const utils = trpc.useUtils();

	// Fetch profile for the referral code
	const profile = trpc.user.getProfile.useQuery();
	// Fetch stats
	const stats = trpc.user.getReferralStats.useQuery();

	const updateCodeMutation = trpc.user.updateReferralCode.useMutation({
		onSuccess: () => {
			toast.success("Referral code updated successfully!");
			utils.user.getProfile.invalidate();
		},
		onError: (error) => {
			toast.error(error.message || "Failed to update referral code");
		},
	});

	const form = useAppForm({
		defaultValues: {
			referralCode: "",
		},
		validators: {
			onChange: UpdateReferralCodeSchema,
		},
		onSubmit: async ({ value }) => {
			updateCodeMutation.mutate({ referralCode: value.referralCode });
		},
	});

	// Copy functionality
	const [copied, setCopied] = useState(false);
	const handleCopy = async () => {
		const code = profile.data?.referralCode;
		if (!code) return;

		const baseUrl = import.meta.env.PROD
			? "https://app.llmgenerator.com"
			: import.meta.env.VITE_FRONTEND_URL || window.location.origin;
		const referralLink = `${baseUrl}/?ref=${code}`;

		try {
			await navigator.clipboard.writeText(referralLink);
			setCopied(true);
			toast.success("Referral link copied!");
			setTimeout(() => setCopied(false), 2000);
		} catch {
			toast.error("Failed to copy link");
		}
	};

	if (profile.isLoading || stats.isLoading) {
		return (
			<PageContainer maxWidth="narrow">
				<BackButton to="/settings" label="Back to Settings" />
				<PageHeader title="Referrals" description="Loading..." />
				<LoadingState />
			</PageContainer>
		);
	}

	const referralCode = profile.data?.referralCode;

	return (
		<PageContainer maxWidth="narrow">
			<BackButton to="/settings" label="Back to Settings" />

			<PageHeader
				title="Refer a Friend"
				description="Share your link and earn credits when friends sign up"
			/>

			<div className="space-y-6">
				{/* Stats Grid */}
				<div className="grid grid-cols-1 gap-4 md:grid-cols-2">
					<Card>
						<CardContent className="flex items-center space-x-4 p-6">
							<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
								<Users className="h-6 w-6 text-primary" />
							</div>
							<div>
								<p className="font-medium text-muted-foreground text-sm">
									Friends Referred
								</p>
								<h3 className="font-bold text-2xl">
									{stats.data?.totalReferred || 0}
								</h3>
							</div>
						</CardContent>
					</Card>

					<Card>
						<CardContent className="flex items-center space-x-4 p-6">
							<div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
								<Coins className="h-6 w-6 text-primary" />
							</div>
							<div>
								<p className="font-medium text-muted-foreground text-sm">
									Credits Earned
								</p>
								<h3 className="font-bold text-2xl">
									{stats.data?.totalCreditsEarned || 0}
								</h3>
							</div>
						</CardContent>
					</Card>
				</div>

				{/* Your Link */}
				<Card>
					<CardHeader>
						<CardTitle className="flex items-center gap-2">
							<Gift className="h-5 w-5 text-primary" />
							Your Referral Link
						</CardTitle>
						<CardDescription>
							Share this link anywhere. You and your friend both receive 100
							bonus credits on signup.
						</CardDescription>
					</CardHeader>
					<CardContent className="space-y-4">
						<div className="flex items-center gap-2">
							<Input
								readOnly
								value={
									referralCode
										? `${import.meta.env.PROD ? "https://app.llmgenerator.com" : import.meta.env.VITE_FRONTEND_URL || window.location.origin}/?ref=${referralCode}`
										: "Loading..."
								}
								className="flex-1 bg-muted/50 font-mono text-sm"
								onClick={(e) => (e.target as HTMLInputElement).select()}
							/>
							<Button onClick={handleCopy} className="shrink-0 gap-2">
								{copied ? (
									<Check className="h-4 w-4" />
								) : (
									<Copy className="h-4 w-4" />
								)}
								{copied ? "Copied" : "Copy Link"}
							</Button>
						</div>
					</CardContent>
				</Card>

				{/* Customize Code */}
				<Card>
					<CardHeader>
						<CardTitle>Customize Your Code</CardTitle>
						<CardDescription>
							Create a memorable referral code. Must be 4-20 alphanumeric
							characters.
						</CardDescription>
					</CardHeader>
					<CardContent>
						<form.AppForm>
							<form
								onSubmit={(e) => {
									e.preventDefault();
									e.stopPropagation();
									form.handleSubmit();
								}}
								className="space-y-4"
							>
								<form.AppField name="referralCode">
									{(field) => (
										<field.FormItem>
											<field.FormLabel>Custom Code</field.FormLabel>
											<div className="flex items-center gap-2">
												<field.FormControl>
													<Input
														placeholder={referralCode || "your-custom-code"}
														value={field.state.value}
														onChange={(e) => field.handleChange(e.target.value)}
														onBlur={field.handleBlur}
														className="max-w-xs font-mono"
													/>
												</field.FormControl>
												<form.Subscribe selector={(state) => state}>
													{(state) => (
														<Button
															type="submit"
															disabled={
																!state.canSubmit ||
																updateCodeMutation.isPending ||
																!field.state.value
															}
														>
															{updateCodeMutation.isPending
																? "Saving..."
																: "Update"}
														</Button>
													)}
												</form.Subscribe>
											</div>
											<field.FormMessage />
										</field.FormItem>
									)}
								</form.AppField>
							</form>
						</form.AppForm>
					</CardContent>
				</Card>
			</div>
		</PageContainer>
	);
}
